import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";

// Models
import {
  CreateControleResiduoRequestModel,
  CreateControleEnergiaRequestModel,
  GetAllControleResiduoResponseModel,
  CreateControleAguaRequestModel,
  GetAllControleEnergiaResponseModel,
  GetAllControleAguaResponseModel,
  GetControleResiduoByIdResponseModel,
} from "../../domain/models/obras_sustentabilidade_model";

// Paginations (Assumindo que você criou os 3 hooks de paginação)

// Schemas/Forms (Assumindo que você tem as tipagens dos forms)

import { useControleAguaListaPagination } from "../hooks/Obras_sustentabilidade/agua_pagination";
import { useControleEnergiaListaPagination } from "../hooks/Obras_sustentabilidade/energia_pagination";
import { useControleResiduoListaPagination } from "../hooks/Obras_sustentabilidade/residuos_pagination";
import { ControleAguaForm } from "../schemas/Obras_sustentabilidade/agua_schema";
import { ControleEnergiaForm } from "../schemas/Obras_sustentabilidade/energia_schema";
import { ControleResiduoForm } from "../schemas/Obras_sustentabilidade/residuos_schema";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { Obra } from "../../domain/models/obra";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { downloadBase64File } from "../../../../common/utils/download_utils";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";

type LoadObraFuncState = {
  idObra?: number;
};

export const useSustentabilidadeViewModel = () => {
  const {
    obras: {
      // Resíduos
      getAllControleResiduoUseCase,
      getControleResiduoByIdUseCase,
      createControleResiduoUseCase,
      updateControleResiduoUseCase,
      deleteControleResiduoUseCase,
      // Energia
      getAllControleEnergiaUseCase,
      getControleEnergiaByIdUseCase,
      createControleEnergiaUseCase,
      updateControleEnergiaUseCase,
      deleteControleEnergiaUseCase,
      // Água
      getAllControleAguaUseCase,
      getControleAguaByIdUseCase,
      createControleAguaUseCase,
      updateControleAguaUseCase,
      deleteControleAguaUseCase,

      getObraByIdUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();
  const location = useLocation();
  const backOrNavigate = useBackOrNavigate();
  const obrasTopHeaderState = useObrasTopHeaderState();

  // Estado Global do Contexto
  const [errorMessage, setErrorMessage] = useState<string>();
  const [idObra, setIdObra] = useState<number>();
  const [selectedObra, setSelectedObra] = useState<Obra>();
  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });

  // O ideal é ter loadings separados para não travar a tela inteira se você atualizar só uma aba
  const [loadingResiduos, setLoadingResiduos] = useState(false);
  const [loadingEnergia, setLoadingEnergia] = useState(false);
  const [loadingAgua, setLoadingAgua] = useState(false);

  type LoadFuncState = {
    idObra?: number;
    Page?: number | null;
    RowsPerPage?: number | null;
  };

  usePageStartBootstrap(async () => {
    await handleReload();
  });

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve(base64.split(",")[1]); // remove prefixo
      };
      reader.onerror = reject;
    });


  // =========================================================================
  // #region CONTROLE DE RESÍDUOS
  // =========================================================================
  const paginationResiduo = useControleResiduoListaPagination();
  const [listaResiduos, setListaResiduos] = useState<GetAllControleResiduoResponseModel>();
  const [abrirModalResiduo, setAbrirModalResiduo] = useState(false);
  const [dadosEditResiduo, setDadosEditResiduo] = useState<GetControleResiduoByIdResponseModel | null>(null);
  const [editmodResiduo, setEditmodResiduo] = useState(false);
  const [savingResiduo, setSavingResiduo] = useState(false);
  const [openDeleteModalResiduo, setOpenDeleteModalResiduo] = useState(false);
  const [idResiduoSelecionado, setIdResiduoSelecionado] = useState<number>();


  const loadObra = useCallback(
    async (funcState?: LoadObraFuncState) => {
      try {
        const filteredIdObra = funcState?.idObra ?? idObra;

        const obra = await getObraByIdUseCase.getObraById(TelasIds.Obras_sustentabilidade, filteredIdObra);

        setSelectedObra(obra);
      } catch (e: unknown) {
        setSelectedObra(undefined);
        throw e;
      }
    },
    [idObra, getObraByIdUseCase],
  );

  const loadResiduos = useCallback(
    async (funcState?: LoadFuncState) => {
      try {
        const filterId = funcState?.idObra ?? idObra;
        const filterPage = funcState?.Page ?? paginationResiduo.page;
        const filterRowsPerPage = funcState?.RowsPerPage ?? paginationResiduo.rowsPerPage;

        const resultado = await getAllControleResiduoUseCase.getAllControleResiduo({
          page: filterPage,
          rowsPerPage: filterRowsPerPage,
          idObra: filterId ?? 0,
        });

        setListaResiduos(resultado);
        paginationResiduo.setRowsPerPage(resultado.pagedItens.pageSize);
        paginationResiduo.setTotalItems(resultado.pagedItens.totalItems);
      } catch (e: unknown) {
        pushErrorNotification("Erro", "Falha ao buscar itens de resíduos!");
      }
    },
    [paginationResiduo, getAllControleResiduoUseCase, idObra, pushErrorNotification],
  );

  const handlePageChangeResiduo = useCallback(
    async (page: number) => {
      setLoadingResiduos(true);
      useControleResiduoListaPagination.setState({ page: page + 1 });
      await loadResiduos({ Page: page + 1 });
      setLoadingResiduos(false);
    },
    [loadResiduos],
  );

  const handleRowsPerPageChangeResiduo = useCallback(
    async (rowsPerPage: number) => {
      setLoadingResiduos(true);
      useControleResiduoListaPagination.setState({ page: 1, rowsPerPage });
      await loadResiduos({ Page: 1, RowsPerPage: rowsPerPage });
      setLoadingResiduos(false);
    },
    [loadResiduos],
  );

  const abrirModalCreateResiduo = useCallback(() => {
    setDadosEditResiduo(null);
    setAbrirModalResiduo(true);
    setEditmodResiduo(false);
  }, []);

  const fecharmodalResiduo = useCallback(() => {
    setAbrirModalResiduo(false);
    setDadosEditResiduo(null);
    setEditmodResiduo(false);
  }, []);


  // #endregion

  // =========================================================================
  // #region CONTROLE DE ENERGIA
  // =========================================================================
  const paginationEnergia = useControleEnergiaListaPagination();
  const [listaEnergia, setListaEnergia] = useState<GetAllControleEnergiaResponseModel>();
  const [abrirModalEnergia, setAbrirModalEnergia] = useState(false);
  const [dadosEditEnergia, setDadosEditEnergia] = useState<ControleEnergiaForm | null>(null);
  const [editmodEnergia, setEditmodEnergia] = useState(false);
  const [savingEnergia, setSavingEnergia] = useState(false);
  const [openDeleteModalEnergia, setOpenDeleteModalEnergia] = useState(false);
  const [idEnergiaSelecionada, setIdEnergiaSelecionada] = useState<number>();

  const setCampoResiduo = useCallback((campo: keyof ControleResiduoForm, valor: any) => {
    setDadosEditResiduo((prev) => {
      if (!prev) return prev;
      return { ...prev, [campo]: valor };
    });
  }, []);

  const loadEnergia = useCallback(
    async (funcState?: LoadFuncState) => {
      try {
        const filterId = funcState?.idObra ?? idObra;
        const filterPage = funcState?.Page ?? paginationEnergia.page;
        const filterRowsPerPage = funcState?.RowsPerPage ?? paginationEnergia.rowsPerPage;
        const resultado = await getAllControleEnergiaUseCase.getAllControleEnergia({
          page: filterPage,
          rowsPerPage: filterRowsPerPage,
          idObra: filterId ?? 0,
        });

        setListaEnergia(resultado);
        paginationEnergia.setRowsPerPage(resultado.itens.pageSize);
        paginationEnergia.setTotalItems(resultado.itens.totalItems);
      } catch (e: unknown) {
        pushErrorNotification("Erro", "Falha ao buscar itens de energia!");
      }
    },
    [paginationEnergia, getAllControleEnergiaUseCase, idObra, pushErrorNotification],
  );


  // #endregion

  // =========================================================================
  // #region CONTROLE DE ÁGUA
  // =========================================================================
  const paginationAgua = useControleAguaListaPagination();
  const [listaAgua, setListaAgua] = useState<GetAllControleAguaResponseModel>();
  const [abrirModalAgua, setAbrirModalAgua] = useState(false);
  const [dadosEditAgua, setDadosEditAgua] = useState<ControleAguaForm | null>(null);
  const [editmodAgua, setEditmodAgua] = useState(false);
  const [savingAgua, setSavingAgua] = useState(false);
  const [openDeleteModalAgua, setOpenDeleteModalAgua] = useState(false);
  const [idAguaSelecionada, setIdAguaSelecionada] = useState<number>();

  const loadAgua = useCallback(
    async (funcState?: LoadFuncState) => {
      try {
        const filterId = funcState?.idObra ?? idObra;
        const filterPage = funcState?.Page ?? paginationAgua.page;
        const filterRowsPerPage = funcState?.RowsPerPage ?? paginationAgua.rowsPerPage;

        const resultado = await getAllControleAguaUseCase.getAllControleAgua({
          page: filterPage,
          rowsPerPage: filterRowsPerPage,
          idObra: filterId ?? 0,
        });

        setListaAgua(resultado);
        paginationAgua.setRowsPerPage(resultado.itens.pageSize);
        paginationAgua.setTotalItems(resultado.itens.totalItems);
      } catch (e: unknown) {
        pushErrorNotification("Erro", "Falha ao buscar itens de água!");
      }
    },
    [paginationAgua, getAllControleAguaUseCase, idObra, pushErrorNotification],
  );


  // #endregion
  // =========================================================================
  // RETORNO DO VIEWMODEL
  // =========================================================================
  const handleItemSubmitResiduo = useCallback(async (form: ControleResiduoForm) => {
    try {
      setSavingResiduo(true);

      let arquivoBase64: string | null = null;

      if (form.arquivo1 instanceof File) {
        arquivoBase64 = await fileToBase64(form.arquivo1);
      }

      const payload: CreateControleResiduoRequestModel = {
        idObra: idObra ?? 0,
        ano: form.ano ?? null,
        idDescricaoMes: form.idDescricaoMes ?? null,
        volumeM3: form.volumeM3 ?? null,
        valorCacamba: form.valorCacamba ?? null,
        qtdeTotal: form.qtdeTotal ?? null,
        volumeTotal: form.volumeTotal ?? null,
        valorTotal: form.valorTotal ?? null,
        efetivoMes: form.efetivoMes ?? null,
        mediaTrabalhador: form.mediaTrabalhador ?? null,
        areaConstruida: form.areaConstruida ?? null,
        mediaArea: form.mediaArea ?? null,
        metaAprovacao: form.metaAprovacao ?? null,
        observacao: form.observacao ?? null,
        arquivo1: arquivoBase64,
        nomeArquivo1: form.arquivo1?.name ?? form.nomeArquivo1 ?? null,
        limparArquivo1: form.limparArquivo1 ?? false,
      };

      if (!editmodResiduo) {
        await createControleResiduoUseCase.createControleResiduo(payload);
      } else {
        await updateControleResiduoUseCase.updateControleResiduo({
          ...payload,
          idControleResiduos: form.idControleResiduos ?? 0,
        });
      }
      pushInfoNotification("Sucesso", "Salvo Com Sucesso");
      await loadResiduos();
      await loadAgua();
      await loadEnergia();
      setAbrirModalResiduo(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar resíduo!"));
    } finally {
      setSavingResiduo(false);
    }
  }, [
    idObra,
    createControleResiduoUseCase,
    updateControleResiduoUseCase,
    editmodResiduo,
    loadResiduos,
    loadAgua,
    loadEnergia,
    pushInfoNotification,
    pushErrorNotification
  ]);

  const atualizarareaResiduo = useCallback(async (area?: number) => {
    try {
      setSavingResiduo(true);
      await updateControleResiduoUseCase.updateControleResiduo({
        areaResiduo: area !== 0 ? area : undefined,
        idObra: idObra ?? 0,
        idControleResiduos: 0,
        ano: null,
        idDescricaoMes: null,
        volumeM3: null,
        valorCacamba: null,
        qtdeTotal: null,
        volumeTotal: null,
        valorTotal: null,
        efetivoMes: null,
        mediaTrabalhador: null,
        areaConstruida: null,
        mediaArea: null,
        metaAprovacao: null,
        observacao: null,
        arquivo1: null,
        nomeArquivo1: null,
        limparArquivo1: false,
      });
      await loadResiduos();
      await loadAgua();
      await loadEnergia();
      pushInfoNotification("Sucesso", "Salvo Com Sucesso");
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar resíduo!"));
    } finally {
      setSavingResiduo(false);
    }
  }, [
    idObra,
    updateControleResiduoUseCase,
    loadResiduos,
    loadAgua,
    loadEnergia,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleEditOpenStateResiduo = useCallback(async (id: number | null, openState: boolean) => {
    if (openState && id != null) {
      setLoadingResiduos(true);
      try {
        const response = await getControleResiduoByIdUseCase.getControleResiduoById({ id });

        setDadosEditResiduo(response);

        setEditmodResiduo(true);
        setAbrirModalResiduo(true);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar item!"));
      } finally {
        setLoadingResiduos(false);
      }
    }
  }, [
    getControleResiduoByIdUseCase,
    pushErrorNotification,
    setDadosEditResiduo,
  ]);


  const handleDownloadArquivo = useCallback(async (
    arquivoNome: string, arquivo: File
  ) => {
    if (!savingResiduo) {
      try {
        const base64File = await fileToBase64(arquivo);
        await downloadBase64File(arquivoNome, base64File);

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao fazer download do arquivo!'));
      }
    };
  }, [
    savingResiduo,
    pushErrorNotification,
  ]);


  const handleDeleteResiduo = useCallback(async () => {
    if (idResiduoSelecionado === undefined) return;
    setLoadingResiduos(true);
    try {
      await deleteControleResiduoUseCase.deleteControleResiduo({ id: idResiduoSelecionado });
      await loadResiduos();
      setOpenDeleteModalResiduo(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir item!")[0]);
    } finally {
      setLoadingResiduos(false);
    }
  }, [idResiduoSelecionado, deleteControleResiduoUseCase, loadResiduos, pushErrorNotification]);

  const handlePageChangeEnergia = useCallback(
    async (page: number) => {
      setLoadingEnergia(true);
      useControleEnergiaListaPagination.setState({ page: page + 1 });
      await loadEnergia({ Page: page + 1 });
      setLoadingEnergia(false);
    },
    [loadEnergia],
  );

  const handleRowsPerPageChangeEnergia = useCallback(
    async (rowsPerPage: number) => {
      setLoadingEnergia(true);
      useControleEnergiaListaPagination.setState({ page: 1, rowsPerPage });
      await loadEnergia({ Page: 1, RowsPerPage: rowsPerPage });
      setLoadingEnergia(false);
    },
    [loadEnergia],
  );

  const abrirModalCreateEnergia = useCallback(() => {
    setDadosEditEnergia(null);
    setAbrirModalEnergia(true);
    setEditmodEnergia(false);
  }, []);

  const fecharmodalEnergia = useCallback(() => {
    setAbrirModalEnergia(false);
    setDadosEditEnergia(null);
    setEditmodEnergia(false);
  }, []);

  const handleItemSubmitEnergia = useCallback(async (form: ControleEnergiaForm) => {
    try {
      setSavingEnergia(true);

      const payload: CreateControleEnergiaRequestModel = {
        idObra: idObra ?? 0,
        ano: form.ano ?? null,
        idDescricaoMes: form.idDescricaoMes ?? null,
        kwH: form.kwH ?? null,
        valor: form.valor ?? null,
        areaConstruida: form.areaConstruida ?? null,
        mediaKwhM2: form.mediaKwhM2 ?? null,
        metaAprovacao: form.metaAprovacao ?? null,
        observacao: form.observacao ?? null,
        efetivoMes: form.efetivoMes ?? null,
        diasMedicao: form.diasMedicao ?? null,
      };

      if (!editmodEnergia) {
        await createControleEnergiaUseCase.createControleEnergia(payload);
      } else {
        await updateControleEnergiaUseCase.updateControleEnergia({
          ...payload,
          idControleEnergia: form.idControleEnergia ?? 0,
        });
      }
      pushInfoNotification("Sucesso", "Salvo Com Sucesso");
      await loadEnergia();
      await loadResiduos();
      await loadAgua();
      setAbrirModalEnergia(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar energia!"));
    } finally {
      setSavingEnergia(false);
    }
  }, [
    idObra,
    editmodEnergia,
    createControleEnergiaUseCase,
    updateControleEnergiaUseCase,
    loadResiduos,
    loadAgua,
    loadEnergia,
    pushInfoNotification,
    pushErrorNotification
  ]);

  const atualizarareaEnergia = useCallback(async (area?: number) => {
    try {
      setSavingEnergia(true);
      await updateControleEnergiaUseCase.updateControleEnergia({
        areaEnergia: area !== 0 ? area : undefined,
        idObra: idObra ?? 0,
        idControleEnergia: 0,
        ano: null,
        idDescricaoMes: null,
        kwH: null,
        valor: null,
        areaConstruida: null,
        mediaKwhM2: null,
        metaAprovacao: null,
        diasMedicao: null,
        efetivoMes: null,
        observacao: null,
      });
      await loadResiduos();
      await loadAgua();
      await loadEnergia();
      pushInfoNotification("Sucesso", "Salvo Com Sucesso");
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar energia!"));
    } finally {
      setSavingEnergia(false);
    }
  }, [
    idObra,
    updateControleEnergiaUseCase,
    loadResiduos,
    loadAgua,
    loadEnergia,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleEditOpenStateEnergia = useCallback(
    async (id: number | null, openState: boolean) => {
      if (openState && id != null) {
        setLoadingEnergia(true);
        try {
          const response = await getControleEnergiaByIdUseCase.getControleEnergiaById({ id });
          setDadosEditEnergia({ ...response } as ControleEnergiaForm);
          setAbrirModalEnergia(true);
          setEditmodEnergia(true);
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar item!"));
        } finally {
          setLoadingEnergia(false);
        }
      }
    },
    [getControleEnergiaByIdUseCase, pushErrorNotification],
  );

  const handleDeleteEnergia = useCallback(async () => {
    if (idEnergiaSelecionada === undefined) return;
    setLoadingEnergia(true);
    try {
      await deleteControleEnergiaUseCase.deleteControleEnergia({ id: idEnergiaSelecionada });
      await loadEnergia();
      setOpenDeleteModalEnergia(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir item!")[0]);
    } finally {
      setLoadingEnergia(false);
    }
  }, [idEnergiaSelecionada, deleteControleEnergiaUseCase, loadEnergia, pushErrorNotification]);

  const handlePageChangeAgua = useCallback(
    async (page: number) => {
      setLoadingAgua(true);
      useControleAguaListaPagination.setState({ page: page + 1 });
      await loadAgua({ Page: page + 1 });
      setLoadingAgua(false);
    },
    [loadAgua],
  );

  const handleRowsPerPageChangeAgua = useCallback(
    async (rowsPerPage: number) => {
      setLoadingAgua(true);
      useControleAguaListaPagination.setState({ page: 1, rowsPerPage });
      await loadAgua({ Page: 1, RowsPerPage: rowsPerPage });
      setLoadingAgua(false);
    },
    [loadAgua],
  );

  const abrirModalCreateAgua = useCallback(() => {
    setDadosEditAgua(null);
    setAbrirModalAgua(true);
    setEditmodAgua(false);
  }, []);

  const fecharmodalAgua = useCallback(() => {
    setAbrirModalAgua(false);
    setDadosEditAgua(null);
    setEditmodAgua(false);
  }, []);

  const handleItemSubmitAgua = useCallback(async (form: ControleAguaForm) => {
    try {
      setSavingAgua(true);

      const payload: CreateControleAguaRequestModel = {
        idObra: idObra ?? 0,
        ano: form.ano ?? null,
        idDescricaoMes: form.idDescricaoMes ?? null,
        volumeM3: form.volumeM3 ?? null,
        valor: form.valor ?? null,
        efetivoMes: form.efetivoMes ?? null,
        areaConstruida: form.areaConstruida ?? null,
        mediaArea: form.mediaArea ?? null,
        mediaTrabalhador: form.mediaTrabalhador ?? null,
        metaAprovacao: form.metaAprovacao ?? null,
        observacao: form.observacao ?? null,
        diasMedicao: form.diasMedicao ?? null,
      };

      if (!editmodAgua) {
        await createControleAguaUseCase.createControleAgua(payload);
      } else {
        await updateControleAguaUseCase.updateControleAgua({
          ...payload,
          idControleAgua: form.idControleAgua ?? 0,
        });
      }
      pushInfoNotification("Sucesso", "Salvo Com Sucesso");
      await loadResiduos();
      await loadAgua();
      await loadEnergia();
      setAbrirModalAgua(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar água!"));
    } finally {
      setSavingAgua(false);
    }
  }, [
    idObra,
    editmodAgua,
    createControleAguaUseCase,
    updateControleAguaUseCase,
    loadResiduos,
    loadAgua,
    loadEnergia,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const atualizarareAagua = useCallback(async (area?: number) => {
    try {
      setSavingAgua(true);
      await updateControleAguaUseCase.updateControleAgua({
        areaAgua: area !== 0 ? area : undefined,
        idObra: idObra ?? 0,
        idControleAgua: 0,
        ano: null,
        idDescricaoMes: null,
        volumeM3: null,
        valor: null,
        efetivoMes: null,
        mediaTrabalhador: null,
        areaConstruida: null,
        mediaArea: null,
        metaAprovacao: null,
        diasMedicao: null,
        observacao: null,
      });
      await loadResiduos();
      await loadAgua();
      await loadEnergia();
      pushInfoNotification("Sucesso", "Salvo Com Sucesso");
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar água!"));
    } finally {
      setSavingAgua(false);
    }
  }, [
    idObra,
    updateControleAguaUseCase,
    loadResiduos,
    loadAgua,
    loadEnergia,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleEditOpenStateAgua = useCallback(
    async (id: number | null, openState: boolean) => {
      if (openState && id != null) {
        setLoadingAgua(true);
        try {
          const response = await getControleAguaByIdUseCase.getControleAguaById({ id });
          setDadosEditAgua({ ...response } as ControleAguaForm);
          setAbrirModalAgua(true);
          setEditmodAgua(true);
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar item!"));
        } finally {
          setLoadingAgua(false);
        }
      }
    },
    [getControleAguaByIdUseCase, pushErrorNotification],
  );

  const handleDeleteAgua = useCallback(async () => {
    if (idAguaSelecionada === undefined) return;
    setLoadingAgua(true);
    try {
      await deleteControleAguaUseCase.deleteControleAgua({ id: idAguaSelecionada });
      await loadAgua();
      setOpenDeleteModalAgua(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir item!")[0]);
    } finally {
      setLoadingAgua(false);
    }
  }, [idAguaSelecionada, deleteControleAguaUseCase, loadAgua, pushErrorNotification]);

  const handleReload = useCallback(async () => {
    const parsedId = idObra ?? Number(new URLSearchParams(location.search).get("id"));

    if (!Number.isInteger(parsedId) || parsedId <= 0) {
      setErrorMessage("Falha ao recuperar id da obra!");
      return;
    }

    setErrorMessage(undefined);
    setLoadingResiduos(true);
    setLoadingEnergia(true);
    setLoadingAgua(true);

    try {
      setIdObra(parsedId);
      await Promise.all([
        loadObra({ idObra: parsedId }),
        initializeReportState(parsedId),
        loadResiduos({ idObra: parsedId }),
        loadEnergia({ idObra: parsedId }),
        loadAgua({ idObra: parsedId }),
      ]);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da página!")[0]);
    } finally {
      setLoadingResiduos(false);
      setLoadingEnergia(false);
      setLoadingAgua(false);
    }
  }, [idObra, location.search, loadObra, initializeReportState, loadResiduos, loadEnergia, loadAgua]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: "/Obras_lista" });
  }, [backOrNavigate]);

  return {
    handleReload,
    handleBack,
    errorMessage,
    idObra,
    selectedObra,
    obrasTopHeaderState,
    obrasReportState,

    // Exports de Resíduos
    residuos: {
      loading: loadingResiduos,
      saving: savingResiduo,
      lista: listaResiduos,
      pagination: paginationResiduo,
      abrirModal: abrirModalResiduo,
      openDeleteModal: openDeleteModalResiduo,
      dadosEditResiduo,
      editmod: editmodResiduo,
      handleDownloadArquivo: handleDownloadArquivo,
      setCampo: setCampoResiduo, // Adicione aqui
      handlePageChange: handlePageChangeResiduo,
      handleRowsPerPageChange: handleRowsPerPageChangeResiduo,
      abrirModalCreate: abrirModalCreateResiduo,
      fecharModal: fecharmodalResiduo,
      handleItemSubmit: handleItemSubmitResiduo,
      handleEditOpenState: handleEditOpenStateResiduo,
      handleDelete: handleDeleteResiduo,
      DeleteOpenModal: (id: number | null, open: boolean) => {
        setOpenDeleteModalResiduo(open);
        if (open) setIdResiduoSelecionado(id ?? undefined);
      },
      AtualizarArea: atualizarareaResiduo,
    },

    // Exports de Energia
    energia: {
      loading: loadingEnergia,
      saving: savingEnergia,
      lista: listaEnergia,
      pagination: paginationEnergia,
      abrirModal: abrirModalEnergia,
      openDeleteModal: openDeleteModalEnergia,
      dadosEdit: dadosEditEnergia,
      editmod: editmodEnergia,
      handlePageChange: handlePageChangeEnergia,
      handleRowsPerPageChange: handleRowsPerPageChangeEnergia,
      abrirModalCreate: abrirModalCreateEnergia,
      fecharModal: fecharmodalEnergia,
      handleItemSubmit: handleItemSubmitEnergia,
      handleEditOpenState: handleEditOpenStateEnergia,
      handleDelete: handleDeleteEnergia,
      DeleteOpenModal: (id: number | null, open: boolean) => {
        setOpenDeleteModalEnergia(open);
        if (open) setIdEnergiaSelecionada(id ?? undefined);
      },
      AtualizarArea: atualizarareaEnergia,
    },

    // Exports de Água
    agua: {
      loading: loadingAgua,
      saving: savingAgua,
      lista: listaAgua,
      pagination: paginationAgua,
      abrirModal: abrirModalAgua,
      openDeleteModal: openDeleteModalAgua,
      dadosEdit: dadosEditAgua,
      editmod: editmodAgua,
      handlePageChange: handlePageChangeAgua,
      handleRowsPerPageChange: handleRowsPerPageChangeAgua,
      abrirModalCreate: abrirModalCreateAgua,
      fecharModal: fecharmodalAgua,
      handleItemSubmit: handleItemSubmitAgua,
      handleEditOpenState: handleEditOpenStateAgua,
      handleDelete: handleDeleteAgua,
      DeleteOpenModal: (id: number | null, open: boolean) => {
        setOpenDeleteModalAgua(open);
        if (open) setIdAguaSelecionada(id ?? undefined);
      },
      AtualizarArea: atualizarareAagua,
    },
  };
};
